'use client';

import React from 'react';

/**
 * Configuration for ResizeObserver utility
 */
export interface ResizeObserverConfig {
  /** Element to observe */
  element: HTMLElement | null;
  /** Callback when element size changes */
  onResize: (width: number, height: number) => void;
  /** Whether to use contentBoxSize (more accurate) or contentRect (fallback) */
  useContentBoxSize?: boolean;
  /** Whether to trigger initial measurement */
  initialMeasurement?: boolean;
}

/**
 * Create ResizeObserver with fallback to window resize listener
 * Provides a consistent API for element size observation
 */
export function createResizeObserver(config: ResizeObserverConfig): () => void {
  const { element, onResize, useContentBoxSize = true, initialMeasurement = true } = config;

  if (!element) {
    return () => {}; // Return empty cleanup function
  }

  // Check if ResizeObserver is available
  if (typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        let width: number;
        let height: number;

        if (useContentBoxSize && entry.contentBoxSize) {
          // Use contentBoxSize for more accurate measurements
          const contentBoxSize = Array.isArray(entry.contentBoxSize)
            ? entry.contentBoxSize[0]
            : entry.contentBoxSize;
          width = contentBoxSize?.inlineSize ?? entry.contentRect.width;
          height = contentBoxSize?.blockSize ?? entry.contentRect.height;
        } else {
          // Fallback to contentRect
          width = entry.contentRect.width;
          height = entry.contentRect.height;
        }

        onResize(width, height);
      }
    });

    // Start observing the element
    resizeObserver.observe(element);

    // Initial measurement if requested
    if (initialMeasurement) {
      const rect = element.getBoundingClientRect();
      onResize(rect.width, rect.height);
    }

    // Return cleanup function
    return () => {
      resizeObserver.disconnect();
    };
  } else {
    // Fallback to window resize listener for older browsers
    let timeoutId: number | undefined;

    const handleResize = () => {
      // Throttle resize events to max 10fps (100ms)
      if (timeoutId) return;

      timeoutId = window.setTimeout(() => {
        timeoutId = undefined;
        if (element) {
          const rect = element.getBoundingClientRect();
          onResize(rect.width, rect.height);
        }
      }, 100);
    };

    // Initial measurement if requested
    if (initialMeasurement) {
      if (element) {
        const rect = element.getBoundingClientRect();
        onResize(rect.width, rect.height);
      }
    }

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Return cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }
}

/**
 * React hook for ResizeObserver with automatic cleanup
 *
 * Observes size changes on the referenced element and calls the onResize callback
 * when dimensions change. Automatically handles cleanup when the component unmounts
 * or the element reference changes.
 *
 * @param elementRef - React ref to the element to observe
 * @param onResize - Callback function called with new width and height
 * @param options - Configuration options for the observer
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const containerRef = useRef<HTMLDivElement>(null);
 *
 *   useResizeObserver(containerRef, (width, height) => {
 *     console.log('Container resized:', { width, height });
 *   });
 *
 *   return <div ref={containerRef}>Resizable content</div>;
 * }
 * ```
 */
export function useResizeObserver(
  elementRef: React.RefObject<HTMLElement>,
  onResize: (width: number, height: number) => void,
  options?: {
    useContentBoxSize?: boolean;
    initialMeasurement?: boolean;
  }
): void {
  React.useEffect(() => {
    return createResizeObserver({
      element: elementRef.current,
      onResize,
      useContentBoxSize: options?.useContentBoxSize ?? true,
      initialMeasurement: options?.initialMeasurement ?? true,
    });
  }, [elementRef, onResize, options?.useContentBoxSize, options?.initialMeasurement]);
}
